import { Tenancy_Rich_Asset_Type_Enum_Ps_Enum } from '@pypestream/api-services';

import type { WorkflowError } from '..';
// Value import straight from the defining module, never the `..` barrel: this
// file sits in the same require cycle as `build-workflow-errors` (table-store →
// utils → here → `..` → table-store), where the barrel's re-exported enum
// binding can still be undefined.
import { NodeColumns } from '../table-store';
import { nodeNumberDisplayLabel } from './node-number-display-label';
import {
  toIntegerShapedNodeNumber,
  nodeNumberOrNull,
} from './node-number-identity';
import { normalizeNodeNumber } from './normalize-node-number';

/**
 * The per-row JSON / node-number errors `validateAndSync` raises on the client,
 * ahead of the compilers (`@TODO` there: temporary until the PIF compiler
 * reports them itself).
 *
 * Extracted from the store method for one reason: it was NOT observable. The
 * three interpolated messages and the four `nodeNumber` fields below are the
 * whole user-visible half of PSTUDIO-3990 — "an error must describe the number
 * as missing, never report a specific false value" — and a mutation test proved
 * every one of them could be reverted to `${nodeNumber ?? 'unknown'}` /
 * `nodeNumber ?? null` with the entire unit suite still green, because the only
 * tests that existed called the HELPERS directly and never this call site.
 * `validateAndSync` is ~400 lines of store state and network work, so the call
 * site could not be reached from a test until it became a pure function.
 */
export interface JsonFieldErrorContext {
  /** Position of the row in the SORTED list — the index the popover's
   *  click-navigation falls back to when there is no id.
   *
   *  Nullable because the call site genuinely cannot always supply it: the map
   *  is built from `sortedRows`, and a row still in `pendingRowsToRender` is not
   *  in it, so the lookup returns `undefined` (it used to be hidden behind an
   *  `as number` cast). `WorkflowError.rowIndex` admits null, and null is the
   *  honest answer — a coerced 0 is a VALID address, so it would send the
   *  popover's click to the first row of the table (PSTUDIO-3990). */
  rowIndex: number | null;
  getColumnIndex: (colId: NodeColumns) => number;
  /** The store's own asset-type predicate, injected rather than re-implemented
   *  so the two can never disagree about which types hold JSON. */
  isJsonAssetType: (assetType: unknown) => boolean;
}

export interface JsonFieldErrorRow {
  rowId: string;
  richAssetType?: unknown;
  richAssetContent?: unknown;
  parameterInput?: unknown;
  analyticsEvent?: unknown;
  nodeNumber?: unknown;
}

export const buildJsonFieldErrors = (
  row: JsonFieldErrorRow,
  { rowIndex, getColumnIndex, isJsonAssetType }: JsonFieldErrorContext
): WorkflowError[] => {
  const {
    rowId,
    richAssetType,
    richAssetContent,
    parameterInput,
    analyticsEvent,
    nodeNumber,
  } = row;
  const errors: WorkflowError[] = [];

  /** Every error here addresses its cell by row id as well, so a node with a
   *  BLANK node number still highlights through `errorsByCellId` — the
   *  number-keyed index has no address for it (PSTUDIO-3991). */
  const at = (colId: NodeColumns, errorMessage: string): WorkflowError => ({
    colId,
    colIndex: getColumnIndex(colId),
    rowIndex,
    errorMessage,
    cellNumber: null,
    // `nodeNumberOrNull`, not `?? null`: the latter catches neither `''` nor
    // `0`, and `buildErrorLabel` prints `Node: <n>` for any non-nil value —
    // which is precisely how the phantom `Node: 0` and the empty `Node: `
    // reached the popover title (PSTUDIO-3990).
    nodeNumber: nodeNumberOrNull(nodeNumber),
    nodeId: rowId,
  });

  const jsonFields: { colId: NodeColumns; value: unknown; label: string }[] = [
    {
      colId: NodeColumns.richAssetContent,
      // Webview supports both JSON and the legacy string format (e.g.
      // "label~url"), so a non-JSON value is not an error for that type.
      value:
        richAssetContent &&
        isJsonAssetType(richAssetType) &&
        richAssetType !== Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Webview
          ? richAssetContent
          : undefined,
      label: 'Asset Content Cell',
    },
    {
      colId: NodeColumns.parameterInput,
      value: parameterInput,
      label: 'Parameter Input cell',
    },
    {
      colId: NodeColumns.analyticsEvent,
      value: analyticsEvent,
      label: 'Analytics Events cell',
    },
  ];

  for (const { colId, value, label } of jsonFields) {
    if (!value || typeof value !== 'string') continue;
    try {
      JSON.parse(value);
    } catch {
      errors.push(
        at(
          colId,
          // `nodeNumberDisplayLabel`, not the raw value: a cleared cell holds
          // `''`, which ended the sentence mid-way ("…, Node #: , please…"),
          // and a `0` was named as though it were a real node number.
          `JSON input error in ${label}, Node #: ${nodeNumberDisplayLabel(
            nodeNumber
          )}, please enter a valid JSON object.`
        )
      );
    }
  }

  // Only probe the backend's range on a value SHAPED like a whole number —
  // blank / 0 / fractional are reported by `deriveNodeNumberErrors` with a
  // message that says what is actually wrong, and letting them fall in here too
  // would put TWO errors on one cell.
  //
  // The gate is `toIntegerShapedNodeNumber`, deliberately NOT the comparison
  // identity and not `toValidNodeNumber`. Both of those answer a different
  // question and would silently delete this check:
  //   - `toValidNodeNumber` requires `> 0`, so it skips every NEGATIVE number,
  //     and negatives are legal here (`-1e19` is below the backend's floor);
  //   - `toNodeNumberIdentity` requires a SAFE integer, and every value large
  //     enough to trip the ceiling is by definition too large to compare — so
  //     gating on it removes the range check from exactly the magnitudes it
  //     exists for.
  // The cost of the split is that such a value can collect this error and the
  // invalid-value one from the derivation; naming the range is worth one extra
  // popover row, and the alternative is no range error at all (PSTUDIO-3990).
  const probeNodeNumber = toIntegerShapedNodeNumber(nodeNumber);
  if (probeNodeNumber !== undefined) {
    try {
      normalizeNodeNumber(probeNodeNumber);
    } catch (e) {
      errors.push(
        at(NodeColumns.nodeNumber, (e as Error).message || 'Unknown error')
      );
    }
  }

  return errors;
};
